import type { ReactNode, RefObject } from 'react';
import { useEffect, useState } from 'react';
import type { TextInput } from 'react-native';

export type TagType = 'user' | 'space' | 'factiii';

type UseTagSuggestionProps = {
  content: string;
  setContent: (content: string) => void;
  inputRef: RefObject<
    HTMLInputElement | HTMLTextAreaElement | TextInput | null
  >;
  TagSuggestionModal: React.ComponentType<{
    type: TagType;
    query: string;
    caretPosition: { top: number; left: number } | null;
    fullWidth?: boolean;
    closeHandler: () => void;
    itemClickHandler: (slug: string, id: number) => void;
  }>;
  getCaretPosition: () => Promise<{ top: number; left: number }>;
  onItemClick?: (type: TagType, id: number, slug: string) => void;
  cursorPosition?: number; // For React Native TextInput
  setCursorPosition?: (position: number) => void; // For React Native TextInput
  resetSearchBar?: boolean;
};

type SuggestionState = {
  visible: boolean;
  type: TagType | null;
  query: string;
  startPos: number;
  endPos: number;
  caretPosition: { top: number; left: number } | null;
};

export const DELIMITERS = {
  user: '@',
  space: 's/',
  factiii: 'f/'
} as const;

const WORD_BOUNDARIES = [' ', '\n', '\t'];

const INITIAL_SUGGESTION_STATE: SuggestionState = {
  visible: false,
  type: null,
  query: '',
  startPos: -1,
  endPos: -1,
  caretPosition: null
};

/**
 * Helper to get current cursor position across web and mobile platforms.
 * On Android, onSelectionChange fires independently of onChangeText, so the
 * cursor position state can lag behind the content by one keystroke. When the
 * effect runs from a content change, cursorPosition may still point to the
 * old position (e.g. cursor=1 when content="f/"). This breaks two-character
 * delimiter detection (s/, f/). Fix: if cursor is behind the content end and
 * appears to be exactly one character behind, the user is typing at the end
 * — use content.length so the full text is considered for tag detection.
 */
const getCursorPosition = (
  input: HTMLInputElement | HTMLTextAreaElement | TextInput,
  fallbackPosition: number,
  cursorPosition?: number
): number => {
  if ('selectionStart' in input) {
    return input.selectionStart ?? fallbackPosition;
  }
  // On mobile (React Native), cursorPosition comes from onSelectionChange state.
  // If it's exactly one behind content length, assume the user just typed a char
  // and the selection event hasn't caught up yet.
  if (cursorPosition !== undefined && cursorPosition === fallbackPosition - 1) {
    return fallbackPosition;
  }
  return cursorPosition ?? fallbackPosition;
};

/**
 * Helper to find word boundary before cursor
 */
const findWordStart = (content: string, cursorPos: number): number => {
  let wordStart = cursorPos - 1;
  while (wordStart >= 0 && !WORD_BOUNDARIES.includes(content[wordStart])) {
    wordStart--;
  }
  return wordStart + 1; // Move to first character after boundary
};

/**
 * Helper to check if delimiter is at valid position (start or after boundary)
 */
const isValidDelimiterPosition = (
  index: number,
  content: string,
  wordStart: number
): boolean => {
  return (
    index === 0 ||
    WORD_BOUNDARIES.includes(content[wordStart + index - 1] ?? '')
  );
};

/**
 * Detect tag type and position from text before cursor
 */
const detectTag = (
  textBeforeCursor: string,
  content: string,
  wordStart: number
): { type: TagType; delimiterPos: number } | null => {
  let detectedType: TagType | null = null;
  let delimiterPos = -1;

  // Check @ (user) - must be at valid position, so "user@host" is not a mention
  const atIndex = textBeforeCursor.lastIndexOf(DELIMITERS.user);
  if (atIndex !== -1 && isValidDelimiterPosition(atIndex, content, wordStart)) {
    detectedType = 'user';
    delimiterPos = atIndex;
  }

  // Check s/ (space) - must be at valid position
  const spaceIndex = textBeforeCursor.lastIndexOf(DELIMITERS.space);
  if (
    spaceIndex !== -1 &&
    (!detectedType || spaceIndex > delimiterPos) &&
    isValidDelimiterPosition(spaceIndex, content, wordStart)
  ) {
    detectedType = 'space';
    delimiterPos = spaceIndex;
  }

  // Check f/ (factiii) - must be at valid position
  const factiiiIndex = textBeforeCursor.lastIndexOf(DELIMITERS.factiii);
  if (
    factiiiIndex !== -1 &&
    (!detectedType || factiiiIndex > delimiterPos) &&
    isValidDelimiterPosition(factiiiIndex, content, wordStart)
  ) {
    detectedType = 'factiii';
    delimiterPos = factiiiIndex;
  }

  return detectedType && delimiterPos !== -1
    ? { type: detectedType, delimiterPos }
    : null;
};

/**
 * The tag being typed immediately before `cursorPos`, or null.
 *
 * Shared so the rich editor and the plain fields that still use this hook
 * (SearchBar, NewRepostModal) detect `@`, `s/` and `f/` by one rule. Offsets
 * are into `text`, which for the rich editor is the current block, not the
 * whole document.
 */
export const tagBeingTyped = (
  text: string,
  cursorPos: number
): { type: TagType; start: number; query: string } | null => {
  const wordStart = findWordStart(text, cursorPos);
  const before = text.slice(wordStart, cursorPos);
  const found = detectTag(before, text, wordStart);
  if (!found) return null;
  return {
    type: found.type,
    start: wordStart + found.delimiterPos,
    query: before.slice(found.delimiterPos + DELIMITERS[found.type].length)
  };
};

/**
 * Update cursor position after content change
 */
const updateCursor = (
  inputRef: RefObject<
    HTMLInputElement | HTMLTextAreaElement | TextInput | null
  >,
  position: number,
  setCursorPosition?: (position: number) => void
) => {
  // Update React Native cursor position
  if (setCursorPosition) {
    setCursorPosition(position);
  }

  // Update web cursor position
  const input = inputRef.current;
  if (input) {
    input.focus();
    if ('setSelectionRange' in input) {
      input.setSelectionRange(position, position);
    }
  }
};

const useTagSuggestion = ({
  content,
  setContent,
  inputRef,
  TagSuggestionModal,
  onItemClick: onItemClickExtension,
  getCaretPosition,
  cursorPosition,
  setCursorPosition,
  resetSearchBar
}: UseTagSuggestionProps) => {
  const [suggestionState, setSuggestionState] = useState<SuggestionState>(
    INITIAL_SUGGESTION_STATE
  );

  useEffect(() => {
    const input = inputRef.current;

    // Reset if no input or empty content
    if (!input || content === '') {
      setSuggestionState(INITIAL_SUGGESTION_STATE);
      return;
    }

    const cursorPos = getCursorPosition(input, content.length, cursorPosition);
    const wordStart = findWordStart(content, cursorPos);
    const textBeforeCursor = content.slice(wordStart, cursorPos);

    // Detect tag delimiter and position
    const tagInfo = detectTag(textBeforeCursor, content, wordStart);

    if (!tagInfo) {
      setSuggestionState(INITIAL_SUGGESTION_STATE);
      return;
    }

    const { type, delimiterPos } = tagInfo;
    const query = textBeforeCursor.slice(
      delimiterPos + DELIMITERS[type].length
    );
    const absoluteStartPos = wordStart + delimiterPos;

    // Get caret position for modal, fallback to null if it fails
    getCaretPosition()
      .then((caretPosition) => {
        setSuggestionState({
          visible: true,
          type,
          query,
          startPos: absoluteStartPos,
          endPos: cursorPos,
          caretPosition
        });
      })
      .catch(() => {
        setSuggestionState({
          visible: true,
          type,
          query,
          startPos: absoluteStartPos,
          endPos: cursorPos,
          caretPosition: null
        });
      });
  }, [content, inputRef, cursorPosition]);

  const handleClose = () => {
    setSuggestionState((prev) => ({ ...prev, visible: false }));
  };

  const handleItemClick = (
    type: TagType,
    slug: string,
    _id: number,
    resetSearchBar: boolean
  ) => {
    const { startPos, endPos } = suggestionState;

    // Reset suggestion state first to prevent re-triggering
    setSuggestionState(INITIAL_SUGGESTION_STATE);

    if (startPos === -1 || endPos === -1) return;

    const before = content.slice(0, startPos);
    const after = content.slice(endPos);

    if (resetSearchBar) {
      // Remove the typed suggestion text
      const newContent = before + after.trimStart();
      setContent(newContent);
      updateCursor(inputRef, startPos, setCursorPosition);
    } else {
      // Replace with selected tag
      const replacement = DELIMITERS[type] + slug;
      const newContent = before + replacement + ' ' + after;
      const newCursorPos = startPos + replacement.length + 1;

      setContent(newContent);
      updateCursor(inputRef, newCursorPos, setCursorPosition);
    }
  };

  const renderSuggestions = (options?: { fullWidth?: boolean }): ReactNode => {
    if (!suggestionState.visible || !suggestionState.type) return null;

    return (
      <TagSuggestionModal
        type={suggestionState.type}
        query={suggestionState.query}
        caretPosition={suggestionState.caretPosition}
        fullWidth={options?.fullWidth}
        closeHandler={handleClose}
        itemClickHandler={(slug: string, id: number) => {
          handleItemClick(suggestionState.type!, slug, id, !!resetSearchBar);
          onItemClickExtension?.(suggestionState.type!, id, slug);
        }}
      />
    );
  };

  return {
    renderSuggestions,
    content,
    setContent
  };
};

export default useTagSuggestion;
